// Founder OS (FOUNDER-OS; 12, ADR-0013; 04 `me/companies?counts=true`, `accounts/*`): the
// account and its portfolio company, the switcher's company list, and the portfolio views — the
// Focus layer, the portfolio org chart with linked head seats, the escalated issues, the cascade
// of portfolio priorities and the founder brief. Every portfolio view is a route of the portfolio
// company (`/v1/companies/{co}/portfolio/…`, `co` = the portfolio company), runs in its tenant
// transaction and reads the control plane's projections of the account's companies (never a
// company's tables: 05 §Founder OS). A drill-down into one company uses that company's own
// routes through the caller's own membership or grant there.
import { z } from "zod";
import {
  AccountCompanyRelation,
  AccountGrantLevel,
  Band,
  CompanyKind,
  FounderBriefCadence,
  GoalState,
  GoalTrack,
  IssueStatus,
  MembershipKind,
  PortfolioProjectionKind,
} from "../enums";
import { FounderBrief } from "../portfolio-rows";
import { NonEmpty, Slug, Timestamp, Uuid } from "../primitives";
import { FounderSharing } from "../rows";
import { CompanyParams } from "./common";
import { defineRoute } from "./route";

const base = "/v1/companies/{co}/portfolio";
const core = { feature: "core", tag: "portfolio" } as const;

// The switcher (04 `me/companies?counts=true`) ------------------------------------------------

/**
 * Where a company sits in the switcher: the portfolio first, then the companies the person owns
 * or works in ("My companies"), then those reached through an agency grant ("Clients via the agency").
 */
export const SwitcherGroup = z.enum(["portfolio", "owned", "member", "client"]);

export const SwitcherCompany = z
  .object({
    company_id: Uuid,
    slug: Slug,
    name: z.string(),
    kind: CompanyKind,
    group: SwitcherGroup,
    /** The agency a `client` row is reached through ("Clients via the agency"). */
    agency_name: z.string().nullable(),
    /** Unread inbox items and pending approvals of the caller there (`cp.membership_counts`). */
    unread: z.int().min(0),
    approvals: z.int().min(0),
    /** The latest company KPI band from the account's projections; null outside the account. */
    band: Band.nullable(),
    /** The founder's order in the portfolio (⌘-number); null outside the account. */
    position: z.int().min(0).nullable(),
  })
  .meta({ id: "SwitcherCompany" });
export type SwitcherCompany = z.infer<typeof SwitcherCompany>;

export const AccountRef = z
  .object({
    id: Uuid,
    name: z.string(),
    portfolio_company_id: Uuid,
    portfolio_slug: Slug,
    /** The caller's account grant (`owner`, `portfolio_member`, `portfolio_viewer`). */
    level: AccountGrantLevel,
  })
  .meta({ id: "AccountRef" });
export type AccountRef = z.infer<typeof AccountRef>;

export const MeCompanies = z
  .object({ account: AccountRef.nullable(), companies: z.array(SwitcherCompany) })
  .meta({ id: "MeCompanies" });
export type MeCompanies = z.infer<typeof MeCompanies>;

export const AccountCreate = z
  .strictObject({ name: NonEmpty.optional().describe("Default: the person's name") })
  .meta({ id: "AccountCreate" });

// The account's companies ------------------------------------------------------------------------

export const HeadSeat = z
  .object({
    seat_id: Uuid,
    title: z.string(),
    holder_name: z.string().nullable(),
    holder_kind: MembershipKind.nullable(),
    /** OQ 32: a DE head seat shows its supervisor; escalations route there. */
    supervisor_title: z.string().nullable(),
    /**
     * FOUNDER-OS phase 1: the company pinned this seat as its head (Setup › Chart); false = the
     * fallback (the filled root seat with the most reports). Absent in older projections.
     */
    pinned: z.boolean().optional(),
    /** An open head seat: since when it has had no holder (null while held). */
    open_since: Timestamp.nullable().optional(),
  })
  .meta({ id: "HeadSeat" });
export type HeadSeat = z.infer<typeof HeadSeat>;

export const PortfolioCompany = z
  .object({
    company_id: Uuid,
    slug: Slug,
    name: z.string(),
    relation: AccountCompanyRelation,
    position: z.int().min(0),
    added_at: Timestamp,
    head_seat: HeadSeat.nullable(),
  })
  .meta({ id: "PortfolioCompany" });
export type PortfolioCompany = z.infer<typeof PortfolioCompany>;

/**
 * Wave 12: a NEW company the founder owns, made and added in one call. It takes the portfolio's
 * reporting currency and calendar; the slug defaults to the name's.
 */
export const PortfolioNewCompany = z
  .strictObject({ name: z.string().trim().min(1).max(120), slug: Slug.optional() })
  .meta({ id: "PortfolioNewCompany" });

/** An existing company (`company_id`), or a new one the caller owns (`create`). */
export const PortfolioCompanyAdd = z
  .union([z.strictObject({ company_id: Uuid }), z.strictObject({ create: PortfolioNewCompany })])
  .meta({ id: "PortfolioCompanyAdd" });

export const PortfolioCompanyParams = z.object({ co: Uuid, company_id: Uuid });

// The Focus layer --------------------------------------------------------------------------------

export const Trend = z.enum(["up", "down", "flat"]);

export const OffTrackPriority = z
  .object({
    goal_id: Uuid,
    title: z.string(),
    track: GoalTrack,
    seat_title: z.string().nullable(),
    owner_name: z.string().nullable(),
  })
  .meta({ id: "OffTrackPriority" });

export const EscalationItem = z
  .object({
    company_id: Uuid,
    company_name: z.string(),
    company_slug: Slug,
    issue_id: Uuid,
    title: z.string(),
    priority: z.int().min(1).max(3),
    status: IssueStatus,
    owner_title: z.string().nullable(),
    owner_name: z.string().nullable(),
    why: z.string().nullable(),
    escalated_at: Timestamp.nullable(),
  })
  .meta({ id: "EscalationItem" });
export type EscalationItem = z.infer<typeof EscalationItem>;

/**
 * An exception that rises to the founder (12 §Focus layer: exceptions only). FOUNDER-OS phase 1
 * adds `vacancy` (the company's head seat is open) and `stale` (no numbers for 7 days).
 */
export const NeedsYouKind = z
  .enum(["kpi", "priority", "escalation", "budget", "approvals", "meeting", "vacancy", "stale"])
  .meta({ id: "NeedsYouKind" });
export type NeedsYouKind = z.infer<typeof NeedsYouKind>;

export const NeedsYou = z.object({ kind: NeedsYouKind, text: z.string() }).meta({ id: "NeedsYou" });

/**
 * FOUNDER-OS phase 1: the projection kinds a company withholds from Founder OS (its "Sharing with
 * Founder OS" setting), so a card says "Withheld" instead of showing a stale number.
 */
export const WithheldKinds = z.array(PortfolioProjectionKind).meta({ id: "WithheldKinds" });

export const FocusCompany = z
  .object({
    company_id: Uuid,
    slug: Slug,
    name: z.string(),
    relation: AccountCompanyRelation,
    band: Band.nullable(),
    kpi_score: z.number().nullable(),
    kpi_period: z.string().nullable(),
    trend: Trend.nullable(),
    priorities: z.object({
      quarter: z.string().nullable(),
      total: z.int().min(0),
      on: z.int().min(0),
      at_risk: z.int().min(0),
      off: z.int().min(0),
      achieved: z.int().min(0),
      off_track: z.array(OffTrackPriority),
    }),
    escalations: z.object({ count: z.int().min(0), items: z.array(EscalationItem) }),
    finance: z
      .object({
        currency: z.string(),
        period_key: z.string(),
        budget_minor: z.int().nullable(),
        actual_minor: z.int(),
        variance_minor: z.int().nullable(),
      })
      .nullable(),
    agents: z.object({
      des: z.int().min(0),
      live: z.int().min(0),
      runs_7d: z.int().min(0),
      spend_month_micros: z.int().min(0),
      approvals_waiting: z.int().min(0),
    }),
    headcount: z.object({
      people: z.int().min(0),
      seats: z.int().min(0),
      open_seats: z.int().min(0),
    }),
    meetings: z.object({
      next_at: Timestamp.nullable(),
      last_at: Timestamp.nullable(),
      overdue: z.boolean(),
    }),
    needs_you: z.array(NeedsYou),
    /** The newest projection behind the row. */
    updated_at: Timestamp.nullable(),
    /** FOUNDER-OS phase 1: what the company does not share (absent in older answers). */
    withheld: WithheldKinds.optional(),
  })
  .meta({ id: "FocusCompany" });
export type FocusCompany = z.infer<typeof FocusCompany>;

export const Focus = z
  .object({
    account_id: Uuid,
    generated_at: Timestamp,
    needs_you_count: z.int().min(0),
    /** Exceptions first: rows with something that needs the founder, then the rest A–Z. */
    companies: z.array(FocusCompany),
    portfolio_priorities: z.object({
      quarter: z.string(),
      total: z.int().min(0),
      on: z.int().min(0),
      at_risk: z.int().min(0),
      off: z.int().min(0),
    }),
  })
  .meta({ id: "Focus" });
export type Focus = z.infer<typeof Focus>;

// The portfolio org chart ------------------------------------------------------------------------

export const PortfolioSeat = z
  .object({
    seat_id: Uuid,
    title: z.string(),
    holder_name: z.string().nullable(),
    supervisor_seat_id: Uuid.nullable(),
  })
  .meta({ id: "PortfolioSeat" });

export const LinkedSeat = z
  .object({
    linked_seat_id: Uuid,
    /** The portfolio seat the card hangs under (the founder seat). */
    portfolio_seat_id: Uuid,
    company_id: Uuid,
    slug: Slug,
    name: z.string(),
    relation: AccountCompanyRelation,
    band: Band.nullable(),
    head_seat: HeadSeat.nullable(),
    priorities: z.object({ total: z.int().min(0), off: z.int().min(0), at_risk: z.int().min(0) }),
    headcount: z.object({
      people: z.int().min(0),
      seats: z.int().min(0),
      open_seats: z.int().min(0),
    }),
    // FOUNDER-OS phase 1: the chart lenses over the card (design §2.4), all from projections.
    /** Health: the company's KPI score (0–100) of its newest week, and the trend. */
    kpi_score: z.number().nullable().optional(),
    trend: Trend.nullable().optional(),
    /** Money: this month's budget vs actual (null: not shared, or no books). */
    finance: z
      .object({
        currency: z.string(),
        budget_minor: z.int().nullable(),
        actual_minor: z.int(),
        variance_minor: z.int().nullable(),
      })
      .nullable()
      .optional(),
    /** Headcount: the digital workforce beside the people. */
    agents: z.object({ des: z.int().min(0), live: z.int().min(0) }).optional(),
    withheld: WithheldKinds.optional(),
    updated_at: Timestamp.nullable().optional(),
  })
  .meta({ id: "LinkedSeat" });
export type LinkedSeat = z.infer<typeof LinkedSeat>;

export const PortfolioChart = z
  .object({
    founder_seat: PortfolioSeat.nullable(),
    /** The portfolio company's other seats (EA, chief of staff, shared services). */
    seats: z.array(PortfolioSeat),
    linked: z.array(LinkedSeat),
    /** FOUNDER-OS phase 1: the caller may move a linked card under another seat (owner). */
    can_edit: z.boolean().optional(),
  })
  .meta({ id: "PortfolioChart" });
export type PortfolioChart = z.infer<typeof PortfolioChart>;

/** FOUNDER-OS phase 1: hang a company's linked card under another seat of the founder chart. */
export const LinkedSeatPatch = z
  .strictObject({ portfolio_seat_id: Uuid })
  .meta({ id: "LinkedSeatPatch" });

/** The link as the founder side may see it (the company's own seat id stays with the company). */
export const LinkedSeatRef = z
  .object({ id: Uuid, company_id: Uuid, portfolio_seat_id: Uuid })
  .meta({ id: "LinkedSeatRef" });

// The roll-up scorecard (FOUNDER-OS phase 1, design §2.4) ----------------------------------------

/**
 * One metric line as the company's `kpi` projection carries it: the client view only (agency
 * metrics never cross), and only when the company shares metric values.
 */
export const PortfolioMetric = z
  .object({
    key: z.string(),
    name: z.string(),
    unit: z.string(),
    direction: z.string(),
    value: z.number().nullable(),
    target: z.number().nullable(),
    /** Target met this period (null: no value or no target). */
    hit: z.boolean().nullable(),
    health: z.enum(["green", "amber", "red"]).nullable(),
    trend: Trend.nullable(),
  })
  .meta({ id: "PortfolioMetric" });
export type PortfolioMetric = z.infer<typeof PortfolioMetric>;

export const PortfolioWeek = z
  .object({
    period_key: z.string(),
    kpi_score: z.number().nullable(),
    band: Band.nullable(),
    completeness: z.number().nullable(),
    withheld: z.boolean(),
  })
  .meta({ id: "PortfolioWeek" });

export const PortfolioScorecardRow = z
  .object({
    company_id: Uuid,
    slug: Slug,
    name: z.string(),
    relation: AccountCompanyRelation,
    band: Band.nullable(),
    kpi_score: z.number().nullable(),
    completeness: z.number().nullable(),
    trend: Trend.nullable(),
    kpi_period: z.string().nullable(),
    /** One per column of `PortfolioScorecard.weeks` (null: no row that week). */
    weeks: z.array(PortfolioWeek.nullable()),
    head_seat: HeadSeat.nullable(),
    head_metrics: z.array(PortfolioMetric),
    company_metrics: z.array(PortfolioMetric),
    /** What the company shares of its numbers: metric values, scores only, or nothing. */
    sharing: z.enum(["metrics", "scores", "withheld"]),
    updated_at: Timestamp.nullable(),
  })
  .meta({ id: "PortfolioScorecardRow" });
export type PortfolioScorecardRow = z.infer<typeof PortfolioScorecardRow>;

export const PortfolioScorecard = z
  .object({
    /** The week columns, oldest first (at most 13: one quarter). */
    weeks: z.array(z.string()).max(13),
    companies: z.array(PortfolioScorecardRow),
  })
  .meta({ id: "PortfolioScorecard" });
export type PortfolioScorecard = z.infer<typeof PortfolioScorecard>;

// Reports › Live (FOUNDER-OS phase 1, design §3.1) -----------------------------------------------

const LiveCompanyRef = {
  company_id: Uuid,
  slug: Slug,
  name: z.string(),
};

export const PortfolioAlert = z
  .object({ ...LiveCompanyRef, kind: NeedsYouKind, text: z.string(), at: Timestamp.nullable() })
  .meta({ id: "PortfolioAlert" });
export type PortfolioAlert = z.infer<typeof PortfolioAlert>;

export const PortfolioMoney = z
  .object({
    currency: z.string(),
    budget_minor: z.int().nullable(),
    actual_minor: z.int(),
    variance_minor: z.int().nullable(),
  })
  .meta({ id: "PortfolioMoney" });

export const PortfolioLive = z
  .object({
    generated_at: Timestamp,
    /** Newest first: every company's `needs_you`, plus open head seats and stale numbers. */
    alerts: z.array(PortfolioAlert),
    health: z.array(
      z.object({
        ...LiveCompanyRef,
        band: Band.nullable(),
        kpi_score: z.number().nullable(),
        trend: Trend.nullable(),
        completeness: z.number().nullable(),
        updated_at: Timestamp.nullable(),
        withheld: z.boolean(),
      }),
    ),
    kpis: z.array(
      z.object({
        ...LiveCompanyRef,
        period_key: z.string().nullable(),
        hit: z.int().min(0),
        miss: z.int().min(0),
        /** The missed metrics by name (CEO seat first, then the company's). */
        misses: z.array(z.string()),
        sharing: z.enum(["metrics", "scores", "withheld"]),
      }),
    ),
    money: z.object({
      rows: z.array(
        z.object({
          ...LiveCompanyRef,
          period_key: z.string().nullable(),
          money: PortfolioMoney.nullable(),
          withheld: z.boolean(),
        }),
      ),
      /** One total per reporting currency (no conversion across currencies). */
      totals: z.array(PortfolioMoney),
      /** Cash needs an accounting or bank connection (decision 8): none yet. */
      cash_connected: z.boolean(),
    }),
    workforce: z.object({
      rows: z.array(
        z.object({
          ...LiveCompanyRef,
          people: z.int().min(0),
          des: z.int().min(0),
          des_live: z.int().min(0),
          ags: z.int().min(0),
          runs_7d: z.int().min(0),
          failed_7d: z.int().min(0),
          spend_month_micros: z.int().min(0),
          hours_week_minutes: z.int().min(0),
          approvals_waiting: z.int().min(0),
          withheld: z.boolean(),
        }),
      ),
    }),
    priorities: z.object({
      portfolio: z.object({
        quarter: z.string(),
        total: z.int().min(0),
        on: z.int().min(0),
        at_risk: z.int().min(0),
        off: z.int().min(0),
      }),
      off_track: z.array(
        z.object({
          ...LiveCompanyRef,
          goal_id: Uuid,
          title: z.string(),
          track: GoalTrack,
          owner_name: z.string().nullable(),
        }),
      ),
    }),
  })
  .meta({ id: "PortfolioLive" });
export type PortfolioLive = z.infer<typeof PortfolioLive>;

// Cascade --------------------------------------------------------------------------------------

export const CascadeLink = z
  .object({
    link_id: Uuid,
    company_id: Uuid,
    company_name: z.string(),
    company_slug: Slug,
    goal_id: Uuid,
    /** From the company's `goals` projection; null when the projection does not carry it. */
    title: z.string().nullable(),
    track: GoalTrack.nullable(),
    state: GoalState.nullable(),
    progress: z.number().min(0).max(1).nullable(),
  })
  .meta({ id: "CascadeLink" });

export const PortfolioGoal = z
  .object({
    goal_id: Uuid,
    title: z.string(),
    track: GoalTrack,
    state: GoalState,
    period_key: z.string().nullable(),
    seat_title: z.string().nullable(),
    /** Roll-up over the linked company priorities (achieved = 1, on track = its milestones). */
    progress: z.number().min(0).max(1).nullable(),
    links: z.array(CascadeLink),
  })
  .meta({ id: "PortfolioGoal" });
export type PortfolioGoal = z.infer<typeof PortfolioGoal>;

export const PortfolioGoals = z
  .object({ quarter: z.string(), goals: z.array(PortfolioGoal) })
  .meta({ id: "PortfolioGoals" });

export const GoalLinkCreate = z
  .strictObject({ portfolio_goal_id: Uuid, company_id: Uuid, company_goal_id: Uuid })
  .meta({ id: "GoalLinkCreate" });

export const GoalLink = z
  .object({
    id: Uuid,
    portfolio_goal_id: Uuid,
    company_id: Uuid,
    company_goal_id: Uuid,
    created_at: Timestamp,
  })
  .meta({ id: "GoalLink" });

// Escalations and the brief ------------------------------------------------------------------

export const PortfolioEscalations = z
  .object({ items: z.array(EscalationItem) })
  .meta({ id: "PortfolioEscalations" });

export const FounderBriefGenerate = z
  .strictObject({ cadence: FounderBriefCadence.optional().describe("Default: daily") })
  .meta({ id: "FounderBriefGenerate" });

export const FounderBriefList = z
  .object({ data: z.array(FounderBrief) })
  .meta({ id: "FounderBriefList" });

/**
 * FOUNDER-OS phase 1: what an operating company shares with Founder OS right now — its own
 * setting, else the default of how it is held (Setup › Company starts its form from this).
 */
export const FounderSharingView = z
  .object({
    effective: FounderSharing,
    source: z.enum(["setting", "owned_default", "client_default"]),
    /** Some live Founder OS account holds the company. */
    held: z.boolean(),
  })
  .meta({ id: "FounderSharingView" });
export type FounderSharingView = z.infer<typeof FounderSharingView>;

export const portfolioRoutes = {
  "companies.founderSharing": defineRoute({
    feature: "core",
    id: "companies.founderSharing",
    method: "get",
    path: "/v1/companies/{co}/founder-sharing",
    tag: "companies",
    summary: "What this company shares with Founder OS now (its setting, else the default)",
    access: "manager",
    params: CompanyParams,
    response: { status: 200, schema: FounderSharingView },
    errors: [],
    emits: [],
  }),
  "me.companies": defineRoute({
    feature: "core",
    id: "me.companies",
    method: "get",
    path: "/v1/me/companies",
    tag: "me",
    summary: "The switcher: the portfolio first, then every company, with counts and health",
    access: "session",
    response: { status: 200, schema: MeCompanies },
    errors: [],
    emits: [],
  }),
  "accounts.create": defineRoute({
    feature: "core",
    id: "accounts.create",
    method: "post",
    path: "/v1/accounts",
    tag: "portfolio",
    summary: "Create the caller's account and its Founder OS portfolio company (one per person)",
    access: "session",
    body: AccountCreate,
    response: { status: 201, schema: AccountRef },
    errors: ["conflict", "forbidden"],
    emits: [
      "company.created",
      "membership.created",
      "access.granted",
      "account.created",
      "role.created",
      "seat.created",
      "seat.assigned",
    ],
  }),
  "portfolio.companies.list": defineRoute({
    ...core,
    id: "portfolio.companies.list",
    method: "get",
    path: `${base}/companies`,
    summary: "The account's companies, in the founder's order",
    access: "viewer",
    params: CompanyParams,
    response: { status: 200, schema: z.object({ data: z.array(PortfolioCompany) }) },
    errors: [],
    emits: [],
  }),
  // 04 `accounts/{id}/companies`: a session route, not a portfolio-company one — it reads the added
  // company in ITS transaction (the caller's own standing there), then writes the portfolio's.
  "accounts.companies.add": defineRoute({
    ...core,
    id: "accounts.companies.add",
    method: "post",
    path: "/v1/accounts/{account_id}/companies",
    summary:
      "Add a company the caller owns (or serves through an agency grant) to the portfolio, or make a new one the caller owns and add it",
    access: "session",
    params: z.object({ account_id: Uuid }),
    body: PortfolioCompanyAdd,
    response: { status: 201, schema: PortfolioCompany },
    errors: ["conflict", "forbidden", "not_found"],
    emits: [
      "portfolio.company_added",
      "linked_seat.created",
      "portfolio_projection.published",
      "company.created",
      "membership.created",
      "access.granted",
      "role.created",
      "seat.created",
      "seat.assigned",
      "metric_def.created",
      "template.created",
    ],
  }),
  "portfolio.companies.remove": defineRoute({
    ...core,
    id: "portfolio.companies.remove",
    method: "delete",
    path: `${base}/companies/{company_id}`,
    summary: "Take a company out of the portfolio (its links end; nothing in the company changes)",
    access: "manager",
    params: PortfolioCompanyParams,
    response: { status: 204 },
    errors: [],
    emits: ["portfolio.company_removed", "linked_seat.removed", "goal.uncascaded"],
  }),
  "portfolio.focus": defineRoute({
    ...core,
    id: "portfolio.focus",
    method: "get",
    path: `${base}/focus`,
    summary: "The Focus layer: one row per company from its projections, exceptions first",
    access: "viewer",
    params: CompanyParams,
    response: { status: 200, schema: Focus },
    errors: [],
    emits: [],
  }),
  "portfolio.chart": defineRoute({
    ...core,
    id: "portfolio.chart",
    method: "get",
    path: `${base}/chart`,
    summary:
      "The portfolio org chart: the founder seat and each company's head seat linked under it",
    access: "viewer",
    params: CompanyParams,
    response: { status: 200, schema: PortfolioChart },
    errors: [],
    emits: [],
  }),
  "portfolio.linkedSeats.update": defineRoute({
    ...core,
    id: "portfolio.linkedSeats.update",
    method: "patch",
    path: `${base}/linked-seats/{id}`,
    summary:
      "Hang a company's linked head seat under another seat of the founder chart (`id`: the link, or the company when it has none yet)",
    access: "manager",
    params: z.object({ co: Uuid, id: Uuid }),
    body: LinkedSeatPatch,
    response: { status: 200, schema: LinkedSeatRef },
    errors: ["conflict"],
    emits: ["linked_seat.moved", "linked_seat.created"],
  }),
  "portfolio.scorecard": defineRoute({
    ...core,
    id: "portfolio.scorecard",
    method: "get",
    path: `${base}/scorecard`,
    summary:
      "The roll-up scorecard: one row per company (13 weeks, the CEO seat's and the company's metrics as shared)",
    access: "viewer",
    params: CompanyParams,
    response: { status: 200, schema: PortfolioScorecard },
    errors: [],
    emits: [],
  }),
  "portfolio.live": defineRoute({
    ...core,
    id: "portfolio.live",
    method: "get",
    path: `${base}/live`,
    summary:
      "Reports › Live: alerts, health, KPIs vs targets, money, workforce and priorities across the companies",
    access: "viewer",
    params: CompanyParams,
    response: { status: 200, schema: PortfolioLive },
    errors: [],
    emits: [],
  }),
  "portfolio.escalations": defineRoute({
    ...core,
    id: "portfolio.escalations",
    method: "get",
    path: `${base}/escalations`,
    summary: "Issues the companies escalated to the portfolio (from their projections)",
    access: "viewer",
    params: CompanyParams,
    response: { status: 200, schema: PortfolioEscalations },
    errors: [],
    emits: [],
  }),
  "portfolio.goals": defineRoute({
    ...core,
    id: "portfolio.goals",
    method: "get",
    path: `${base}/goals`,
    summary: "Portfolio priorities of the quarter with their cascaded company priorities",
    access: "viewer",
    params: CompanyParams,
    query: z.object({
      quarter: z
        .string()
        .regex(/^\d{4}-Q[1-4]$/)
        .max(7)
        .optional()
        .describe("Default: the current quarter"),
    }),
    response: { status: 200, schema: PortfolioGoals },
    errors: [],
    emits: [],
  }),
  "portfolio.goalLinks.create": defineRoute({
    ...core,
    id: "portfolio.goalLinks.create",
    method: "post",
    path: `${base}/goal-links`,
    summary: "Cascade a portfolio priority into a company priority",
    access: "manager",
    params: CompanyParams,
    body: GoalLinkCreate,
    response: { status: 201, schema: GoalLink },
    errors: ["conflict"],
    emits: ["goal.cascaded"],
  }),
  "portfolio.goalLinks.delete": defineRoute({
    ...core,
    id: "portfolio.goalLinks.delete",
    method: "delete",
    path: `${base}/goal-links/{id}`,
    summary: "Remove a cascade link",
    access: "manager",
    params: z.object({ co: Uuid, id: Uuid }),
    response: { status: 204 },
    errors: [],
    emits: ["goal.uncascaded"],
  }),
  "portfolio.briefs.list": defineRoute({
    ...core,
    id: "portfolio.briefs.list",
    method: "get",
    path: `${base}/briefs`,
    summary: "The founder briefs, newest first (the last 30)",
    access: "viewer",
    params: CompanyParams,
    response: { status: 200, schema: FounderBriefList },
    errors: [],
    emits: [],
  }),
  "portfolio.briefs.generate": defineRoute({
    ...core,
    id: "portfolio.briefs.generate",
    method: "post",
    path: `${base}/briefs`,
    summary: "Generate the founder brief from the projections now",
    access: "manager",
    params: CompanyParams,
    body: FounderBriefGenerate,
    response: { status: 201, schema: FounderBrief },
    errors: [],
    emits: ["founder_brief.generated"],
  }),
} as const;
